export type DocumentScope = 'contact' | 'class' | 'room' | 'discussion';

/** Où un document est rattaché — une cible par portée. */
export type DocumentTarget =
  | { kind: 'contact'; peerId: string }
  | { kind: 'class'; classId: string }
  | { kind: 'room'; roomId: string }
  | { kind: 'discussion'; discussionId: string };

/** Ligne `documents` (snake_case côté PostgREST) avec ses noms joints. */
export interface RawDocumentRow {
  id: string;
  uploaded_by: string;
  scope: DocumentScope;
  peer_id: string | null;
  class_id: string | null;
  room_id: string | null;
  discussion_id: string | null;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  storage_path: string;
  created_at: string;
  uploader?: { display_name: string | null } | null;
  peer?: { display_name: string | null } | null;
  class?: { name: string | null } | null;
  room?: { title: string | null; starts_at: string | null } | null;
  discussion?: { title: string | null } | null;
}

export interface DocumentRow {
  id: string;
  uploadedBy: string;
  uploaderName: string | null;
  scope: DocumentScope;
  peerId: string | null;
  peerName: string | null;
  classId: string | null;
  className: string | null;
  roomId: string | null;
  roomTitle: string | null;
  roomStartsAt: string | null;
  discussionId: string | null;
  discussionTitle: string | null;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  storagePath: string;
  createdAt: string;
}

export function documentFromRow(row: RawDocumentRow): DocumentRow {
  return {
    id: row.id,
    uploadedBy: row.uploaded_by,
    uploaderName: row.uploader?.display_name ?? null,
    scope: row.scope,
    peerId: row.peer_id,
    peerName: row.peer?.display_name ?? null,
    classId: row.class_id,
    className: row.class?.name ?? null,
    roomId: row.room_id,
    roomTitle: row.room?.title ?? null,
    roomStartsAt: row.room?.starts_at ?? null,
    discussionId: row.discussion_id,
    discussionTitle: row.discussion?.title ?? null,
    fileName: row.file_name,
    mimeType: row.mime_type,
    sizeBytes: Number(row.size_bytes),
    storagePath: row.storage_path,
    createdAt: row.created_at,
  };
}

export type DocumentErrorCode =
  | 'invalid_file'
  | 'upload_failed'
  | 'save_failed'
  | 'delete_failed'
  | 'sign_failed'
  | 'list_failed';

/** Erreur typée : `message` est déjà en français, affichable telle quelle. */
export class DocumentError extends Error {
  constructor(
    public readonly code: DocumentErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'DocumentError';
  }
}
